<script lang="ts">
import Component from 'vue-class-component'
import { VisTreeNode } from '@/types'
import { hierarchy, HierarchyCircularNode, pack } from 'd3-hierarchy'
import VisualisationCanvas from '@/components/public/Visualisations/VisualisationCanvas.vue'
import {
  calculateClusterSize,
  findLargestRadiiByLevel,
  NestedBubble,
  NestedBubbleMetadata,
} from '@/components/public/Visualisations/NestedBubbles'

const MARGIN_SCALE = 0.05
const CIRCLE_PADDING = 30
const MIN_DISPLAY_LEVEL = 2

// eslint-disable-next-line no-use-before-define
@Component<NestedBubbles>({
  watch: {
    rootPackedNode: {
      handler(nodes: HierarchyCircularNode<VisTreeNode>) {
        this.rootPackedNodeUpdated(nodes)
      },
      immediate: true,
    },
    maxDisplayLevel() {
      this.updateBubblesAppearance()
    },
    storyPath() {
      this.updateBubblesAppearance()
    },
  },
})
export default class NestedBubbles extends VisualisationCanvas {
  bubbles: NestedBubble[] = []
  largestRadiiByLevel: Map<number, number> = new Map<number, number>()

  get rootVisTreeNode(): VisTreeNode | null {
    return this.filterNonPositiveValueNodes(
      this.$typedStore.visualisationData.filteredRootVisTreeNode,
    )
  }

  get rootPackedNode(): HierarchyCircularNode<VisTreeNode> {
    const packLayout = pack<VisTreeNode>()
      .size([this.minimumCanvasSideLength, this.minimumCanvasSideLength])
      .padding(CIRCLE_PADDING)

    if (this.rootVisTreeNode) {
      const d3RootNode = hierarchy(this.rootVisTreeNode).sum((d) => d.value!)
      return packLayout(d3RootNode)
    }
    return packLayout(hierarchy({} as VisTreeNode))
  }

  get levelCount() {
    return this.$typedStore.visualisationData.levelCount
  }

  get clusterSize() {
    return calculateClusterSize(this.rootPackedNode.children ?? [])
  }

  get additionalScale(): number {
    const widthScale = this.containerSizeOnCanvas.width / this.clusterSize.width
    const heightScale = this.containerSizeOnCanvas.height / this.clusterSize.height
    return Math.min(widthScale, heightScale) - MARGIN_SCALE
  }

  get userZoomScaleToDisplayCirclesByLevel(): Map<number, number> {
    const desiredCircleSize = this.minimumCanvasSideLength * 0.125
    const result = new Map<number, number>()

    this.largestRadiiByLevel.forEach((largestRadiusAtLevel, level) => {
      const scale = desiredCircleSize / (largestRadiusAtLevel * 2)
      result.set(level, scale / Math.max(this.additionalScale, 1))
    })

    return result
  }

  get maxDisplayLevel() {
    let result = MIN_DISPLAY_LEVEL

    for (const [level, userZoomScaleToDisplayCircles] of this
      .userZoomScaleToDisplayCirclesByLevel) {
      if (this.userZoomScale > userZoomScaleToDisplayCircles && level > result) {
        result = level
      }
    }

    return result
  }

  get filterLuminosity() {
    return (
      (this.$typedStore.activeVisualisation.visualisationStore.app.config.searchSettings
        .lightness ?? 1) * 100
    )
  }

  recenter() {
    if (this.storyPath.isRootOrSubroot) {
      this.startFocusAnimation(this.defaultUserZoomScale, this.defaultPosition)
    } else {
      this.focusOnSelectedEntities()
    }
  }

  private filterNonPositiveValueNodes(node: VisTreeNode): VisTreeNode | null {
    if (node.children) {
      node.children = node.children
        .map(this.filterNonPositiveValueNodes)
        .filter((child): child is VisTreeNode => child !== null) // Type guard to filter out nulls
    }
    return (node.value !== undefined && node.value > 0) ||
      (node.children && node.children.length > 0)
      ? node
      : null
  }

  private rootPackedNodeUpdated(nodes: HierarchyCircularNode<VisTreeNode>) {
    this.loading = true
    this.reset()
    this.bubbles = []
    this.largestRadiiByLevel = findLargestRadiiByLevel(nodes)
    this.createBubbles(nodes)
    this.updateBubblesAppearance()
    this.loading = false
  }

  private createBubbles(node: HierarchyCircularNode<VisTreeNode>, depth = 0) {
    if (!node.data.path.isRoot) {
      this.bubbles.push(
        new NestedBubble(
          new NestedBubbleMetadata(
            node,
            this.maxDisplayLevel,
            this.filterLuminosity,
            this.userZoomScale,
          ),
          this.addShape,
        ),
      )
    }

    if (node.children) {
      node.children.forEach((child) => {
        this.createBubbles(child, depth + 1)
      })
    }
  }

  private updateBubblesAppearance() {
    const selectedBubbles = this.bubbles.filter((x) => x.path.equals(this.storyPath))
    this.bubbles.forEach((bubble) => {
      const isSelectedOrAncestorOfSelected = selectedBubbles.some(
        (selectedBubble) =>
          bubble.path.equals(selectedBubble.path) || bubble.path.isAncestorOf(selectedBubble.path),
      )

      bubble.updateAppearance(
        isSelectedOrAncestorOfSelected,
        this.maxDisplayLevel,
        this.filterLuminosity,
        this.userZoomScale,
      )
    })
  }
}
</script>
